import React, { useCallback, useEffect, useRef } from 'react';
import { App as AntdApp } from "antd";
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";
import 'react-phone-input-2/lib/style.css';

import Navigation from './routes/patientRoutes/Navigation';
import useNotificationsSocket from "./notifications/hooks/useNotificationsSocket";
import { getNotificationRoute } from "./notifications/getNotificationRoute";
import { markReadLocal } from "./notifications/redux/notificationsSlice";
import { markNotificationRead } from "./notifications/redux/notificationsThunks";
import { fetchNotifications } from "./notifications/redux/notificationsThunks";

const ASSISTANT_INVITATION_PATH = '/assistants/invitation';

// Routes où le hard-reset ne doit jamais se déclencher :
// flux d'activation assistant + toute page assistant qui peut
// légitimement afficher un loader/modale pendant plus de 1.6s
// (chargement des disponibilités, etc.)
const HARD_RESET_EXEMPT_PATTERNS = [
  '/assistants/invitation',
  '/assistant', // couvre /assistant/availability, /assistant/login, etc.
];

function isHardResetExempt(pathname) {
  return HARD_RESET_EXEMPT_PATTERNS.some((pattern) => pathname.includes(pattern));
}

function hasPersistentUIBlockers() {
  if (document.body.style.pointerEvents === 'none' || document.documentElement.style.pointerEvents === 'none') {
    return true;
  }

  const candidates = [
    '.driver-overlay',
    '.driver-popover',
    '.driver-stage',
    '.driver-highlighted-element',
    '.driver-active-element',
    '.modal-overlay',
    '.modal-container',
    '.results-modal-overlay',
    '.results-modal-container',
    '.doc-type-modal-overlay',
    '.doc-type-modal-container',
    '.ant-modal-root',
    '.ant-modal-mask',
    '.ant-modal-wrap',
    '.ant-drawer-mask',
    '.mobile-sidebar-backdrop',
  ];

  return candidates.some((selector) => document.querySelector(selector));
}

// driver.js overlay/popover artifacts should never legitimately outlive the
// page that started them — safe to force-strip on every route change,
// regardless of whether that page's own tour cleanup ran successfully.
const DRIVER_ARTIFACT_SELECTORS = [
  '.driver-overlay',
  '.driver-popover',
  '.driver-stage',
  '.driver-highlighted-element',
  '.driver-active-element',
];

function forceCleanupDriverArtifacts() {
  DRIVER_ARTIFACT_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  });

  document.body.classList.remove('driver-active');
  document.documentElement.classList.remove('driver-active');

  if (document.body.style.pointerEvents === 'none') {
    document.body.style.pointerEvents = '';
  }
  if (document.documentElement.style.pointerEvents === 'none') {
    document.documentElement.style.pointerEvents = '';
  }
}

function runSingleHardResetForPath(path) {
  const key = `__medilink_unblock_reload__:${path}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  window.location.replace(path);
}

function NavigationUnblocker() {
  const location = useLocation();
  const navIntentRef = useRef(false);

  useEffect(() => {
    const onPointerDownCapture = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const navTarget = target.closest('a,button,[role="tab"],[role="link"],.st-btn,.ant-menu-item,.menu-item,.rr-item');
      if (!navTarget) return;

      navIntentRef.current = true;
    };

    document.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
    };
  }, []);

  // ✅ CORRIGÉ : on réinitialise l'intention de navigation à CHAQUE
  // changement de route. Sans ça, un clic sur la page A (ex: le bouton
  // "créer le mot de passe") continue à armer le watchdog sur la page B
  // qui suit (ex: AssistantAvailabilityPage), et un loader/modale
  // parfaitement normal sur la page B peut déclencher un rechargement
  // complet non désiré, qui efface le state Redux en mémoire.
  useEffect(() => {
    navIntentRef.current = false;
    // A driver.js tour is tied to the page that started it — if its overlay
    // survived past a route change (e.g. its own cleanup threw), the new
    // page renders underneath it and looks "stuck" even though navigation
    // actually succeeded. Force-strip it here, unconditionally, since this
    // watchdog's hard-reset below only fires when navigation *fails*
    // outright, not when it succeeds but a leftover overlay masks it.
    forceCleanupDriverArtifacts();
  }, [location.pathname]);

  useEffect(() => {
    const pathKey = `${location.pathname}${location.search}${location.hash || ''}`;

    // ✅ CORRIGÉ : on exempte les pages assistant (activation + espace
    // assistant) de ce hard-reset, car elles affichent légitimement des
    // loaders/modales pendant le chargement initial des données.
    if (isHardResetExempt(location.pathname)) {
      return undefined;
    }

    const hardResetWatchdog = setTimeout(() => {
      if (!navIntentRef.current) return;
      if (!hasPersistentUIBlockers()) {
        navIntentRef.current = false;
        return;
      }

      runSingleHardResetForPath(pathKey);
    }, 1600);

    return () => {
      clearTimeout(hardResetWatchdog);
    };
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function NotificationSocketBridge() {
  const { notification } = AntdApp.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleClickRef = useRef(null);

  const onNotification = useCallback(
    (item) => {
      notification.open({
        key: `notification-${item?._id || item?.id || Date.now()}`,
        message: item?.title || "New notification",
        description: item?.body || item?.message || "",
        placement: "topRight",
        onClick: () => handleClickRef.current?.(item),
      });
    },
    [notification]
  );

  const { handleNotificationClick } = useNotificationsSocket({
    enabled: true,
    onNotification,
  });

  useEffect(() => {
    handleClickRef.current = handleNotificationClick;
  }, [handleNotificationClick]);

  const openNotificationTarget = useCallback(
    (item) => {
      const doctorToken =
        document.cookie.includes("doctorAccessToken=") ||
        document.cookie.includes("doctor_token=") ||
        localStorage.getItem("doctorAccessToken") ||
        localStorage.getItem("doctor_token");
      const patientToken =
        document.cookie.includes("accessToken=") ||
        document.cookie.includes("auth_token=") ||
        localStorage.getItem("patientAccessToken") ||
        localStorage.getItem("patient_token");

      if (!doctorToken && !patientToken) {
        const role =
          doctorToken || item?.recipientRole === "doctor" || item?.metadata?.recipientRole === "doctor"
            ? "doctor"
            : "patient";
        const route = getNotificationRoute(item, role);
        if (route) {
          const id = item?._id || item?.id;
          if (id) {
            dispatch(markReadLocal(String(id)));
            dispatch(markNotificationRead({ id: String(id) }))
              .unwrap()
              .catch((error) => {
                console.error("[NOTIF-PUSH] Failed to mark notification read", error);
              });
          }
          navigate(role === "doctor" ? "/LoginDoctor" : "/LoginPatient", {
            replace: true,
            state: { redirectTo: route },
          });
        }
        return;
      }

      handleClickRef.current?.(item);
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    if (!window.isSecureContext) {
      console.warn(
        "[NOTIF-PUSH] Notifications and service workers require a secure context."
      );
      notification.warning({
        key: "notification-secure-context",
        message: "Secure connection required",
        description:
          "Browser notifications require HTTPS. Use the secure staging URL to enable push notifications.",
        duration: 0,
      });
      return undefined;
    }
    if (!("serviceWorker" in navigator)) {
      notification.warning({
        key: "notification-service-worker-unsupported",
        message: "Browser notifications unavailable",
        description: "This browser does not support service workers.",
      });
      return undefined;
    }
    const onMessage = (event) => {
      if (event.data?.type === "NOTIFICATION_CLICK" && event.data.notificationId) {
        dispatch(fetchNotifications({ skip: 0, limit: 50 }))
          .unwrap()
          .then((result) => {
            const item = result?.items?.find((entry) => String(entry?._id || entry?.id) === String(event.data.notificationId));
            if (item) openNotificationTarget(item);
          })
          .catch(() => {});
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    navigator.serviceWorker
      .register("/service-worker.js")
      .catch((error) => {
        console.error("[NOTIF-PUSH] Service worker registration failed", error);
        notification.error({
          key: "notification-service-worker-error",
          message: "Browser notifications unavailable",
          description: error?.message || "Service worker registration failed.",
        });
      });

    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [dispatch, notification, openNotificationTarget]);

  useEffect(() => {
    const notificationId = new URLSearchParams(window.location.search).get("notificationId");
    if (!notificationId) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("notificationId");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    dispatch(fetchNotifications({ skip: 0, limit: 50 }))
      .unwrap()
      .then((result) => {
        const item = result?.items?.find((entry) => String(entry?._id || entry?.id) === String(notificationId));
        if (item) openNotificationTarget(item);
      })
      .catch(() => {});
  }, [dispatch, openNotificationTarget]);

  return null;
}

function AssistantInvitationNewTabGuard() {
  useEffect(() => {
    const onClickCapture = (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const href = anchor.getAttribute('href') || '';
      if (!href) return;

      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (!url.pathname.includes(ASSISTANT_INVITATION_PATH)) return;

      const targetAttr = String(anchor.getAttribute('target') || '').toLowerCase();
      if (targetAttr === '_blank') return;

      event.preventDefault();
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    };

    document.addEventListener('click', onClickCapture, true);
    return () => {
      document.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  return null;
}

function App() {
  return (
    <Router>
      <NotificationSocketBridge />
      <NavigationUnblocker />
      <AssistantInvitationNewTabGuard />
      <Navigation />
    </Router>
  );
}

export default App;
